import React, { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { Button, Checkbox } from "@mui/material";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";
import { useTranslation } from "react-i18next";
import { translate } from "../../utils/utils";
import Typography from "@mui/material/Typography";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  requestDeletingUseCase,
  requestDuplicateUseCase,
} from "../../redux/actions/useCaseActionCreators";
import { requestRemoveUseCaseFromQuizz } from "../../redux/actions/quizzesActionCreators";
import { Chip } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import DragIcon from "../../assets/DragIcon.js";
import UseCaseDetails from "../../components/useCase/UseCaseDetails.js";
import DeleteModal from "../../components/useCase/DeleteModal.js";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#F3FBF9",
    color: "#B1B4BC",
    height: "50px",
    padding: "0px 16px 0px 16px",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {},
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

export default function CustomizedTables({
  data,
  disabled,
  head,
  isRelatedCase,
  quizz,
  quizzId,
  handleUseCases,
  newVersion,
  isMobile,
}) {
  const { t } = useTranslation();
  const id_owner = parseInt(localStorage.getItem("orgaId"));
  const storedUser = localStorage.getItem("user");
  const history = useHistory();
  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const dispatch = useDispatch();

  const handleDelete = () => {
    if (quizz) {
      const useCases = data?.map((el) => el.id);

      handleUseCases(data.filter((el) => el.id !== selectedRow.id));
    } else {
      dispatch(requestDeletingUseCase(selectedRow.id));
    }
    setOpenDeleteModal(false);
  };

  const getColor = (level) => {
    if (level === "Easy") {
      return "acceptable";
    } else if (level === "Medium") {
      return "moderate";
    } else if (level === "Hard") {
      return "critical";
    } else {
      return "neutral";
    }
  };

  const role =
    JSON.parse(storedUser).role === "admin" ||
    JSON.parse(storedUser).role === "enterprise_user" ||
    JSON.parse(storedUser).role === "cytailor_admin" ||
    JSON.parse(storedUser).role === "enterprise_admin";

  const handleDragEnd = (results) => {
    if (!results.destination) return;
    let tempUseCase = [...data];
    let [selectedRow] = tempUseCase.splice(results.source.index, 1);
    tempUseCase.splice(results.destination.index, 0, selectedRow);
    handleUseCases(tempUseCase);
  };

  return (
    <DragDropContext onDragEnd={(results) => handleDragEnd(results)}>
      <TableContainer
        component={Paper}
        className="scrollhost"
        sx={{
          width: "100%",
          height: "550px",
          minWidth: "300px",
          "&.MuiTableContainer-root": {
            borderRadius: "0px",
            boxShadow: "none",
          },
        }}
      >
        <Table
          stickyHeader
          aria-label="customized table"
          sx={{
            minWidth: "300px",
            "& .MuiTableRow-root:hover": {
              backgroundColor: "#DDF6F0",
              cursor: "pointer",
            },
          }}
        >
          <TableHead>
            <TableRow>
              {head.map((el) => (
                <StyledTableCell align={el.align}>
                  {t(el.value)}
                </StyledTableCell>
              ))}
            </TableRow>
          </TableHead>
          <Droppable droppableId="tbody">
            {(provided) => (
              <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                {data?.map((row, index) => (
                  <Draggable
                    draggableId={row.title}
                    index={index}
                    key={row.title}
                  >
                    {(provided) =>
                      isMobile ? (
                        <StyledTableRow
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          key={row.id}
                          hover
                          onClick={() => {
                            setSelectedRow(row);
                            setOpenDetailsModal(true);
                          }}
                          role="checkbox"
                          tabIndex={-1}
                        >
                          {isRelatedCase && (
                            <StyledTableCell align="left">
                              <Checkbox
                                onClick={(e) => e.stopPropagation()}
                                color="success"
                              />
                            </StyledTableCell>
                          )}

                          <StyledTableCell align="left">
                            <Typography variant="title3">
                              {row[translate("title")] || t("not_specified")}
                            </Typography>
                          </StyledTableCell>

                          <StyledTableCell align="center">
                            {row.organization_id === id_owner && role && (
                              <div style={{ display: "flex" }}>
                                <Button
                                  fullwidth
                                  variant="outlined"
                                  color="button"
                                  size="small"
                                  sx={{ marginRight: "5px" }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    history.push(`/make-usecase/${row.id}`);
                                  }}
                                >
                                  <ModeEditOutlineOutlinedIcon />
                                </Button>

                                <Tooltip title={t("duplicate")}>
                                  <Button
                                    fullwidth
                                    variant="outlined"
                                    color="info"
                                    size="small"
                                    sx={{ marginRight: "5px" }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      dispatch(
                                        requestDuplicateUseCase(row, history)
                                      );
                                    }}
                                  >
                                    <ContentCopyIcon />
                                  </Button>
                                </Tooltip>

                                <Button
                                  fullwidth
                                  variant="outlined"
                                  color="deleteButton"
                                  size="small"
                                  onClick={(e) => {
                                    setSelectedRow(row);
                                    setOpenDeleteModal(true);
                                    e.stopPropagation();
                                  }}
                                >
                                  <DeleteForeverOutlinedIcon />
                                </Button>
                              </div>
                            )}
                            {quizz && (
                              <Button
                                variant="outlined"
                                color="deleteButton"
                                size="small"
                                sx={{ height: "40px", width: "100%" }}
                                onClick={(e) => {
                                  setSelectedRow(row);
                                  setOpenDeleteModal(true);
                                  e.stopPropagation();
                                }}
                              >
                                <DeleteForeverOutlinedIcon />
                                {t("remove")}
                              </Button>
                            )}
                          </StyledTableCell>
                        </StyledTableRow>
                      ) : (
                        <StyledTableRow
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          key={row.id}
                          hover
                          onClick={() => {
                            setSelectedRow(row);
                            setOpenDetailsModal(true);
                          }}
                          role="checkbox"
                          tabIndex={-1}
                        >
                          {isRelatedCase && (
                            <StyledTableCell align="left">
                              <Checkbox
                                onClick={(e) => e.stopPropagation()}
                                color="success"
                              />
                            </StyledTableCell>
                          )}
                          <StyledTableCell align="center">
                            <DragIcon />
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            <Typography variant="title3">
                              {row[translate("title")] || t("not_specified")}
                            </Typography>
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            <Typography variant="title3">
                              {row.source}
                            </Typography>
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            <Typography variant="title3">
                              {row.threshold}
                            </Typography>
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            <Typography variant="title3">
                              {row.coefficient}
                            </Typography>
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            {row?.difficulty_level && (
                              <Chip
                                label={t(row?.difficulty_level)}
                                variant="outlined"
                                color={getColor(row?.difficulty_level)}
                                icon={<FiberManualRecordIcon />}
                              />
                            )}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            <Typography variant="title3">
                              {row?.weakness?.[translate("title")] ||
                                t("not_specified")}
                            </Typography>
                          </StyledTableCell>

                          <StyledTableCell align="center">
                            {row.organization_id === id_owner && role && (
                              <div style={{ display: "flex" }}>
                                <Button
                                  fullwidth
                                  variant="outlined"
                                  color="button"
                                  size="small"
                                  sx={{ marginRight: "5px" }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    history.push(`/make-usecase/${row.id}`);
                                  }}
                                >
                                  <ModeEditOutlineOutlinedIcon />
                                </Button>

                                <Tooltip title={t("duplicate")}>
                                  <Button
                                    fullwidth
                                    variant="outlined"
                                    color="info"
                                    size="small"
                                    sx={{ marginRight: "5px" }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      dispatch(
                                        requestDuplicateUseCase(row, history)
                                      );
                                    }}
                                  >
                                    <ContentCopyIcon />
                                  </Button>
                                </Tooltip>

                                <Button
                                  fullwidth
                                  variant="outlined"
                                  color="deleteButton"
                                  size="small"
                                  onClick={(e) => {
                                    setSelectedRow(row);
                                    setOpenDeleteModal(true);
                                    e.stopPropagation();
                                  }}
                                >
                                  <DeleteForeverOutlinedIcon />
                                </Button>
                              </div>
                            )}
                            {quizz && (
                              <Button
                                variant="outlined"
                                color="deleteButton"
                                size="small"
                                sx={{ height: "40px", width: "100%" }}
                                onClick={(e) => {
                                  setSelectedRow(row);
                                  setOpenDeleteModal(true);
                                  e.stopPropagation();
                                }}
                              >
                                <DeleteForeverOutlinedIcon />
                                {t("remove")}
                              </Button>
                            )}
                          </StyledTableCell>
                        </StyledTableRow>
                      )
                    }
                  </Draggable>
                ))}
                {provided.placeholder}
              </TableBody>
            )}
          </Droppable>
        </Table>

        <UseCaseDetails
          open={openDetailsModal}
          handleClose={() => setOpenDetailsModal(false)}
          rowDetails={selectedRow}
          id_owner={id_owner}
          company={JSON.parse(storedUser).entreprise}
        />
        <DeleteModal
          open={openDeleteModal}
          handleClose={() => setOpenDeleteModal(false)}
          id={selectedRow.id}
          title={selectedRow.title}
          description={selectedRow.description}
          handleDelete={() => handleDelete(data)}
          buttonLabel={t("delete_use_case")}
        />
      </TableContainer>
    </DragDropContext>
  );
}
